import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Share2, Sparkles, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

type CacheCard = {
  kind: "meme" | "fact";
  emoji: string;
  headline: string;
  body: string;
};

/** Curated, classroom-safe market humour + genuinely useful trivia. */
const CARDS: CacheCard[] = [
  { kind: "meme", emoji: "📉", headline: "\"It's a long-term investment now.\"", body: "— every investor, three minutes after the stock fell 8%." },
  { kind: "fact", emoji: "🕘", headline: "NSE trades for 6 hours 15 minutes", body: "9:15 AM to 3:30 PM IST. Most of the day's volatility lands in the first and last 30 minutes." },
  { kind: "meme", emoji: "🧠", headline: "Portfolio: 1 stock. Conviction: infinite.", body: "Diversification is boring right up to the day it saves you." },
  { kind: "fact", emoji: "🏦", headline: "Sensex started at 100", body: "Base value 100 in 1978-79. Compounding, not luck, did the rest." },
  { kind: "meme", emoji: "☕", headline: "Checked portfolio 14 times today", body: "The chart did not care. It never does." },
  { kind: "fact", emoji: "🔁", headline: "Rule of 72", body: "Divide 72 by the annual return to see how many years your money takes to double. 12% → about 6 years." },
  { kind: "meme", emoji: "🚀", headline: "Bought at the top. Called it 'entry'.", body: "Averaging down is a plan. Hoping is not." },
  { kind: "fact", emoji: "📊", headline: "Volume tells you conviction", body: "A big price move on tiny volume usually has no follow-through." },
  { kind: "meme", emoji: "🦊", headline: "FinFox reviewed your trade", body: "It said: \"Could you explain that in one line?\" You could not. That is the lesson." },
  { kind: "fact", emoji: "🧾", headline: "Circuit limits exist", body: "NSE freezes trading in a stock beyond set % moves in a day to cool down panic and mania alike." },
];

const SEEN_KEY = "finplay:memecache:found";

interface MemeCacheProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MemeCache = ({ open, onOpenChange }: MemeCacheProps) => {
  const [index, setIndex] = useState(0);
  const [firstFind, setFirstFind] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!open) return;
    setIndex(Math.floor(Math.random() * CARDS.length));
    if (!localStorage.getItem(SEEN_KEY)) {
      localStorage.setItem(SEEN_KEY, new Date().toISOString());
      setFirstFind(true);
    }
  }, [open]);

  const card = CARDS[index % CARDS.length];

  const share = async () => {
    const text = `${card.headline} — ${card.body} (found in FinPlay's secret Meme Cache)`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "FinPlay Meme Cache", text });
        return;
      }
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied", description: "Card copied — go share it." });
    } catch {
      /* user dismissed the share sheet */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm rounded-2xl p-0 overflow-hidden [&>button]:hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <p className="text-sm font-semibold">Meme Cache</p>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">secret</span>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            aria-label="Close Meme Cache"
            className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {firstFind && (
          <div className="mx-4 mt-3 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 text-[12px] text-foreground">
            You found the Meme Cache. Double-tap FinFox any time to come back.
          </div>
        )}

        <div className="p-4">
          <div className="rounded-2xl border border-border bg-muted/40 p-5 text-center min-h-[190px] flex flex-col items-center justify-center gap-3">
            <span className="text-4xl" aria-hidden>{card.emoji}</span>
            <p className="text-base font-semibold leading-snug">{card.headline}</p>
            <p className="text-[13px] text-muted-foreground leading-snug">{card.body}</p>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {card.kind === "meme" ? "Market humour" : "Did you know"}
            </span>
          </div>

          <div className="flex items-center gap-2 mt-4">
            <Button variant="outline" size="icon" aria-label="Previous card" onClick={() => setIndex((i) => (i - 1 + CARDS.length) % CARDS.length)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" className="flex-1 gap-2" onClick={share}>
              <Share2 className="h-4 w-4" /> Share
            </Button>
            <Button variant="outline" size="icon" aria-label="Next card" onClick={() => setIndex((i) => (i + 1) % CARDS.length)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <p className="mt-3 text-center text-[10px] text-muted-foreground">
            For fun and learning only — nothing here is investment advice.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MemeCache;
